import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Share2, Gift } from "lucide-react";

export default function ShareBar({ onClaimed }) {
  const { user, shareStatus, claimShare } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!user) return null;

  const shareUrl = window.location.origin;
  const shareText = "Listening to 'If We Knew' — Joey's unhinged dating diary podcast. First 10 episodes are free.";

  const handleShare = async () => {
    if (shareStatus?.available || shareStatus?.used_episode != null) {
      // re-share is fine but no extra credit
      if (navigator.share) {
        try { await navigator.share({ title: "If We Knew", text: shareText, url: shareUrl }); } catch { /* user cancel */ }
      } else {
        await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        toast.success("link copied — paste it anywhere");
      }
      return;
    }

    setBusy(true);
    try {
      let didShare = false;
      if (navigator.share) {
        try {
          await navigator.share({ title: "If We Knew", text: shareText, url: shareUrl });
          didShare = true;
        } catch (err) {
          if (err.name === "AbortError") { setBusy(false); return; }
        }
      }
      if (!didShare) {
        await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        toast.success("link copied to clipboard");
      }
      // grant the credit
      const res = await claimShare();
      toast.success(res.message || "1 free episode unlocked — pick any locked one ♡");
      onClaimed?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not claim share credit");
    } finally {
      setBusy(false);
    }
  };

  // Variant: credit available banner
  if (shareStatus?.available) {
    return (
      <div className="bg-hotpink text-white p-5 md:p-6 border-2 border-white shadow-[6px_6px_0px_#FFFFFF] flex flex-col md:flex-row items-start md:items-center justify-between gap-3" data-testid="share-credit-banner">
        <div className="flex items-center gap-3">
          <Gift size={28} className="flex-shrink-0"/>
          <div>
            <div className="font-outfit font-extrabold text-lg uppercase tracking-wide">you've got 1 free episode!</div>
            <div className="scribble text-white/95 text-xl">pick any locked one below ↓ and tap "use free unlock"</div>
          </div>
        </div>
      </div>
    );
  }

  // Variant: already used
  if (shareStatus?.used_episode != null) {
    return (
      <div className="bg-coal border border-white/10 p-4 flex items-center gap-3 text-white/70 font-outfit text-sm" data-testid="share-used-banner">
        <Gift size={18} className="text-hotpink"/>
        <span>thanks for sharing — your free unlock was used on episode {shareStatus.used_episode}.</span>
        <button onClick={handleShare} data-testid="share-again-btn" className="ml-auto text-hotpink hover:underline">share again →</button>
      </div>
    );
  }

  // Variant: not yet claimed
  return (
    <div className="bg-coal border border-hotpink/40 p-5 md:p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4" data-testid="share-cta-banner">
      <div className="flex items-center gap-3">
        <Share2 size={26} className="text-hotpink flex-shrink-0"/>
        <div>
          <div className="font-outfit font-bold text-white text-base md:text-lg">Share the series → get 1 free episode</div>
          <p className="scribble text-hotpink text-lg">spread the chaos. unlock any $1 episode on us.</p>
        </div>
      </div>
      <button
        onClick={handleShare}
        disabled={busy}
        data-testid="share-claim-btn"
        className="bg-hotpink hover:bg-hotpink_dark text-white font-outfit font-extrabold uppercase tracking-widest text-xs px-6 py-3 shadow-[4px_4px_0px_#FFFFFF] hover:shadow-[6px_6px_0px_#FFFFFF] transition-all flex items-center gap-2 disabled:opacity-60"
      >
        <Share2 size={16}/> {busy ? "..." : "share & claim"}
      </button>
    </div>
  );
}
